/**
 * Validation Engine
 *
 * Runs a series of checks against transformed rows and returns
 * { errors: [], warnings: [], rows: [], summary: {} }
 *
 * Currently uses static reference data. Will switch to D1 lookups
 * when the Worker API is wired up.
 */
import { PROFILES } from '../profiles/index';
import { EXCLUDED_ACCOUNTS } from './excluded-accounts';

// Revenue account prefixes (5xxxxx series)
const isRevenueAccount = (acct) => /^5\d{5}$/.test(String(acct));

/**
 * Validate transformed rows for a given profile.
 */
export function validate(rows, profileKey, meta = {}) {
  const profileDef = PROFILES[profileKey];
  const errors = [];
  const warnings = [];

  let totalAccounts = 0;

  rows.forEach((row, i) => {
    const rowNum = i + 1;

    // Required fields
    profileDef.requiredFields.forEach((field) => {
      if (!row[field] && row[field] !== 0) {
        errors.push({
          row: i,
          field,
          severity: 'error',
          message: `Required field "${field}" is empty.`,
        });
      }
    });

    // Account-level checks
    const acct = String(row.account ?? row.account_no ?? '');
    if (acct) {
      totalAccounts++;

      // Excluded account check
      if (EXCLUDED_ACCOUNTS.has(acct)) {
        errors.push({
          row: i,
          field: 'account',
          severity: 'error',
          message: `Account ${acct} is on the exclusion list (ORA-20001).`,
        });
      }

      // Account format check (expect 6-digit)
      if (!/^\d{4,6}$/.test(acct)) {
        warnings.push({
          row: i,
          field: 'account',
          severity: 'warning',
          message: `Account "${acct}" doesn't match expected format (4-6 digits).`,
        });
      }
    }

    // Unit/Entity check (non-empty, numeric-ish)
    const unit = String(row.unit ?? row.unit_id ?? '');
    if (unit && !/^\d+$/.test(unit) && !/^[A-Za-z0-9]+$/.test(unit)) {
      warnings.push({
        row: i,
        field: 'unit',
        severity: 'warning',
        message: `Unit "${unit}" has unexpected format.`,
      });
    }

    // Amount checks (for JE profiles)
    if (profileKey.includes('je') || profileKey.includes('payroll')) {
      const debit = parseFloat(row.debit ?? 0);
      const credit = parseFloat(row.credit ?? 0);
      if (isNaN(debit) && row.debit !== '' && row.debit !== undefined) {
        errors.push({ row: i, field: 'debit', severity: 'error', message: `Non-numeric debit value.` });
      }
      if (isNaN(credit) && row.credit !== '' && row.credit !== undefined) {
        errors.push({ row: i, field: 'credit', severity: 'error', message: `Non-numeric credit value.` });
      }
      // Lines worth $0 are filtered out by the transformer, so a null amount
      // here means no usable amount was found at all — a data problem, not an
      // intentional zero. Exported as 0, but blocking until it's resolved.
      // Skipped when `amount` is a required field for the profile: the
      // required-field check above already reported this row.
      const amountAlreadyReported = profileDef.requiredFields.includes('amount');
      if (!amountAlreadyReported && (row.amount === null || row.amount === undefined)) {
        errors.push({
          row: i,
          field: 'amount',
          severity: 'error',
          message: `Line has no usable amount. Check the Amount (or Debit/Credit) column for this row.`,
        });
      }
    }

    // Budget amount checks
    if (profileKey === 'gl9300c-budget') {
      const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
      months.forEach((m) => {
        const val = row[m];
        if (val !== '' && val !== undefined && val !== null && isNaN(Number(val))) {
          errors.push({
            row: i,
            field: m.toUpperCase(),
            severity: 'error',
            message: `Non-numeric budget amount in ${m.toUpperCase()}: "${val}"`,
          });
        }
      });
    }
  });

  // JE balance check — sums the same signed `amount` the transformer writes to
  // the output file, so validation can never diverge from what's exported.
  if (profileKey.includes('je') || profileKey.includes('payroll')) {
    let total = 0;
    rows.forEach((row) => {
      total += parseFloat(row.amount ?? 0) || 0;
    });
    if (Math.abs(total) > 0.01) {
      errors.push({
        row: null,
        field: 'Balance',
        severity: 'error',
        message: `Journal entry amounts are out of balance by $${Math.abs(total).toFixed(2)} (sum should be zero).`,
      });
    }
  }

  return {
    errors,
    warnings,
    rows,
    summary: {
      totalRows: rows.length,
      totalAccounts,
      errorCount: errors.length,
      warningCount: warnings.length,
      // Set by the JE/payroll transformers — surfaced so a dropped line is
      // never invisible.
      droppedZeroLines: meta.droppedZeroLines ?? 0,
    },
  };
}
